import { createContext, useContext, useEffect } from "react";

/*
 * Keys a page answers to, for the one shortcuts panel. A page with keys (the master inbox, a
 * campaign's preview) registers them while it is on screen and can use them, and `?` then opens the
 * panel with that list. Nothing registered, no panel: a page with no keys never offers a list of keys
 * it does not have.
 *
 * `keys` is written as it is drawn: "Shift + I", "J / K". Entries that share a `group` are listed
 * together, parted from the next group by a hairline; an entry without one stands in the list alone.
 */
export type ShortcutEntry = {
  readonly keys: string;
  readonly does: string;
  /** What the keys act on, for a screen reader; the panel shows the groups only as their parting. */
  readonly group?: string;
};

export const PageShortcutsContext = createContext<
  (entries: readonly ShortcutEntry[] | null) => void
>(() => undefined);

/** Lists `entries` in the shortcuts panel while this component is mounted and `entries` is set. */
export function usePageShortcuts(entries: readonly ShortcutEntry[] | null): void {
  const register = useContext(PageShortcutsContext);
  useEffect(() => {
    register(entries);
    return () => register(null);
  }, [register, entries]);
}
